/**
 * Structured-403 handling for the free-tier waitlist (§9, docs/plans/
 * 2026-07-29-free-tier-cap-enforcement.md). Generation-triggering routes
 * reject waitlisted users with HTTP 403 and a JSON body
 * `{ waitlisted: true, reason: 'waitlisted', message: '...' }` instead of a
 * generic error — this module detects that shape and turns it into a
 * distinguishable Error so callers can open the pricing/upgrade surface
 * with waitlist copy instead of a generic error toast.
 *
 * Amplify v6's REST API client throws for 4xx/5xx responses, and the JSON
 * body shows up in different shapes depending on which code path threw
 * (already-parsed object, JSON string, or a ReadableStream) — this mirrors
 * the ad hoc parsing already done in services/api.js and
 * services/scheduleService.js.
 */

/**
 * Inspect a caught Amplify API error for the waitlisted-403 body. Returns
 * the parsed `{waitlisted, reason, message}` body, or null if this isn't
 * one (wrong status, unparsable body, or a genuinely different error).
 * Never throws.
 */
export async function parseWaitlistError(rawError) {
  try {
    const body = rawError?.response?.body;
    if (!body) return null;

    let parsed;
    if (typeof body === 'object' && !(body instanceof ReadableStream)) {
      parsed = body;
    } else if (typeof body === 'string') {
      parsed = JSON.parse(body);
    } else if (typeof body.json === 'function') {
      parsed = await body.json();
    } else if (body instanceof ReadableStream) {
      const reader = body.getReader();
      const chunks = [];
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (value) chunks.push(value);
        done = streamDone;
      }
      const text = new TextDecoder().decode(new Uint8Array(chunks.flatMap((c) => Array.from(c))));
      parsed = JSON.parse(text);
    }

    return parsed && parsed.waitlisted === true ? parsed : null;
  } catch (parseError) {
    return null;
  }
}

/**
 * Build an Error carrying `.waitlisted = true` (and `.waitlistMessage`) from
 * a parsed waitlisted-403 body, so UI callers can branch on
 * `error.waitlisted` instead of string-matching a message.
 */
export function waitlistedError(body) {
  const message = body?.message || "You're on the free-tier waitlist — upgrade to Plus to skip the line.";
  const err = new Error(message);
  err.waitlisted = true;
  err.waitlistMessage = body?.message || null;
  return err;
}
